import type {
  CustomFurnitureItem,
  FrontKind as FrontKindT,
  Furniture,
  FurnitureCategory,
  MaterialId,
  Opening,
  OpeningKind,
  OpeningStyle,
  ParamValue,
  RoomSetting,
  TemplateId,
  WallMount,
  WallMountCategory,
} from "./types";
import { defaultSectionalModules } from "./furniture/modules";
import { centerModules } from "./furniture/resolve";

/**
 * Which functional edge of a piece faces the user. The room-canvas convention
 * is: at `rotation: 0`, the front edge is the `+y` edge (length = `width`).
 * Rotation rotates this clockwise. Catalog presets are authored so the
 * advertised front (seat, drawer face, headboard's opposite, etc.) sits on
 * the `+y` edge before rotation.
 *
 * Used by:
 * - The canvas front-of-furniture marker — hidden when `frontKind === "none"`.
 * - The AI feng shui generator + ranker — sent on the wire so the model
 *   reasons about commanding position, back-to-door, etc. against the actual
 *   front edge rather than guessing from the category name.
 * - The door-clearance validator — `drawer` and `door` pieces additionally
 *   need their front-clearance strip kept clear of door-swing zones.
 */
export type FrontKind = FrontKindT;
// Values:
//   "none"   — symmetric / non-directional: ottomans, tables, rugs, lamps, plants.
//   "seat"   — seat/use face: sofas, loveseats, armchairs, dining chairs, desks.
//   "sleep"  — bed foot at +y; headboard at -y.
//   "drawer" — drawer face pulls outward from the +y edge: dressers.
//   "door"   — hinged door swings outward from +y: wardrobes, fridges, washers, stoves.
//   "screen" — display surface faces +y: TV stand.
//   "shelf"  — open shelf face is the +y edge: bookshelves.

/** Front-kinds whose front edge needs unobstructed clearance to be USABLE
 *  (you have to pull a drawer or swing a door outward into the room). The
 *  door-clearance validator drops placements where any door-swing rectangle
 *  intrudes into this front-clearance strip. */
export const FRONT_KINDS_NEED_CLEARANCE: ReadonlySet<FrontKind> = new Set([
  "drawer",
  "door",
]);

export interface FurniturePreset {
  name: string;
  category: FurnitureCategory;
  width: number; // inches
  depth: number;
  height: number;
  color: string;
  icon: string;
  /** Which edge faces the user; see {@link FrontKind}. */
  frontKind: FrontKind;
  /** Parametric template that generates the piece's parts. */
  template: TemplateId;
  /** Template params overriding the template's defaults. */
  params?: Record<string, ParamValue>;
  /** Default surface material. */
  material: MaterialId;
  /** When true the piece is placed as a *modular* sectional (snap-together
   *  modules the user can rearrange on the canvas) instead of a parametric
   *  template. `template` is then only used for the picker's grouping. */
  modular?: boolean;
}

export interface MountPreset {
  name: string;
  category: WallMountCategory;
  width: number;
  height: number;
  /** Suggested floor offset (center). */
  defaultFromFloor: number;
  color: string;
}

export interface OpeningPreset {
  name: string;
  kind: OpeningKind;
  /** "cutout" is an open passage: no leaf and no swing. "gate" is a fence leaf. */
  style?: OpeningStyle;
  width: number;
  height: number;
  /** Center from floor in inches. Doors stay at height/2 (sit on the floor). */
  defaultFromFloor: number;
}

/** Real-world average dimensions in inches.
 *
 *  Convention reminder (see {@link FrontKind}): for any preset with
 *  `frontKind !== "none"`, the advertised front edge is the `+y` edge at
 *  `rotation: 0` (length = `width`). Width is always the along-front
 *  dimension; depth is front-to-back. */
export const FURNITURE_PRESETS: FurniturePreset[] = [
  // Seating
  { name: "3-seat sofa", category: "seating", width: 84, depth: 36, height: 34, color: "#4f6c8f", icon: "sofa", frontKind: "seat", template: "sofa", params: { seats: 3 }, material: "fabric" },
  { name: "Loveseat", category: "seating", width: 60, depth: 36, height: 34, color: "#4f6c8f", icon: "sofa", frontKind: "seat", template: "sofa", params: { seats: 2 }, material: "fabric" },
  { name: "Sectional (L)", category: "seating", width: 100, depth: 64, height: 34, color: "#5b6f8a", icon: "sofa", frontKind: "seat", template: "sectional", params: { layout: "l-right", seats: 3 }, material: "fabric" },
  { name: "Sectional (U)", category: "seating", width: 120, depth: 72, height: 34, color: "#5b6f8a", icon: "sofa", frontKind: "seat", template: "sectional", params: { layout: "u", seats: 3 }, material: "fabric" },
  { name: "Modular sectional", category: "seating", width: 96, depth: 72, height: 34, color: "#6b7d96", icon: "sofa", frontKind: "seat", template: "sectional", material: "boucle", modular: true },
  { name: "Chaise lounge", category: "seating", width: 30, depth: 64, height: 30, color: "#7c8fa8", icon: "sofa", frontKind: "seat", template: "sofa", params: { seats: 1, arms: "left" }, material: "velvet" },
  { name: "Armchair", category: "seating", width: 34, depth: 36, height: 34, color: "#6b8aab", icon: "armchair", frontKind: "seat", template: "armchair", material: "fabric" },
  { name: "Ottoman", category: "seating", width: 28, depth: 22, height: 18, color: "#8aa3c0", icon: "square", frontKind: "none", template: "ottoman", material: "leather" },
  { name: "Dining chair", category: "seating", width: 18, depth: 20, height: 36, color: "#8f7355", icon: "chair", frontKind: "seat", template: "chair", material: "wood-mid" },
  { name: "Bar stool", category: "seating", width: 15, depth: 15, height: 30, color: "#5c4a3f", icon: "square", frontKind: "none", template: "stool", material: "wood-dark" },
  { name: "Bench", category: "seating", width: 48, depth: 16, height: 18, color: "#9a7a52", icon: "square", frontKind: "none", template: "bench", material: "wood-mid" },

  // Tables
  { name: "Coffee table", category: "tables", width: 48, depth: 24, height: 18, color: "#8a6a47", icon: "table", frontKind: "none", template: "table", params: { shape: "rect" }, material: "wood-mid" },
  { name: "Round coffee table", category: "tables", width: 36, depth: 36, height: 18, color: "#8a6a47", icon: "table", frontKind: "none", template: "table", params: { shape: "round" }, material: "wood-mid" },
  { name: "Side table", category: "tables", width: 20, depth: 20, height: 24, color: "#8a6a47", icon: "table", frontKind: "none", template: "table", params: { shape: "rect" }, material: "wood-mid" },
  { name: "Dining table (4)", category: "tables", width: 60, depth: 36, height: 30, color: "#a07b54", icon: "table", frontKind: "none", template: "table", params: { shape: "rect" }, material: "wood-light" },
  { name: "Dining table (6)", category: "tables", width: 72, depth: 40, height: 30, color: "#a07b54", icon: "table", frontKind: "none", template: "table", params: { shape: "rect" }, material: "wood-light" },
  { name: "Round dining table", category: "tables", width: 48, depth: 48, height: 30, color: "#a07b54", icon: "table", frontKind: "none", template: "table", params: { shape: "round" }, material: "wood-light" },
  { name: "Oval dining table", category: "tables", width: 84, depth: 42, height: 30, color: "#a07b54", icon: "table", frontKind: "none", template: "table", params: { shape: "oval" }, material: "wood-light" },
  // Desks have shallow drawers AND a chair side, both on +y; tagging as
  // `drawer` puts them under the front-clearance check (you don't want a
  // door swinging into where you'd pull a drawer or push the chair back).
  { name: "Desk", category: "tables", width: 60, depth: 28, height: 30, color: "#9a7a52", icon: "table", frontKind: "drawer", template: "desk", params: { layout: "straight" }, material: "wood-mid" },
  { name: "Corner desk", category: "tables", width: 60, depth: 60, height: 30, color: "#9a7a52", icon: "table", frontKind: "drawer", template: "desk", params: { layout: "l-right" }, material: "wood-mid" },

  // Beds
  { name: "Twin bed", category: "beds", width: 38, depth: 75, height: 25, color: "#c2a17a", icon: "bed", frontKind: "sleep", template: "bed", params: { pillows: 1 }, material: "fabric" },
  { name: "Full bed", category: "beds", width: 54, depth: 75, height: 25, color: "#c2a17a", icon: "bed", frontKind: "sleep", template: "bed", params: { pillows: 2 }, material: "fabric" },
  { name: "Queen bed", category: "beds", width: 60, depth: 80, height: 25, color: "#c2a17a", icon: "bed", frontKind: "sleep", template: "bed", params: { pillows: 2 }, material: "fabric" },
  { name: "King bed", category: "beds", width: 76, depth: 80, height: 25, color: "#c2a17a", icon: "bed", frontKind: "sleep", template: "bed", params: { pillows: 2 }, material: "fabric" },
  { name: "Nightstand", category: "beds", width: 22, depth: 18, height: 26, color: "#a48560", icon: "square", frontKind: "none", template: "nightstand", material: "wood-mid" },

  // Storage
  { name: "Bookshelf", category: "storage", width: 32, depth: 12, height: 72, color: "#6e574a", icon: "bookshelf", frontKind: "shelf", template: "bookshelf", material: "wood-dark" },
  { name: "Dresser", category: "storage", width: 60, depth: 18, height: 32, color: "#6e574a", icon: "drawer", frontKind: "drawer", template: "dresser", material: "wood-dark" },
  { name: "Wardrobe", category: "storage", width: 48, depth: 24, height: 78, color: "#5c4a3f", icon: "drawer", frontKind: "door", template: "wardrobe", material: "wood-dark" },
  { name: "TV stand", category: "storage", width: 60, depth: 18, height: 24, color: "#3a3a44", icon: "tv-stand", frontKind: "screen", template: "tv-stand", material: "matte" },

  // Appliance
  { name: "Refrigerator", category: "appliance", width: 36, depth: 30, height: 70, color: "#cfd2d8", icon: "fridge", frontKind: "door", template: "fridge", material: "metal" },
  { name: "Washer", category: "appliance", width: 27, depth: 28, height: 38, color: "#cfd2d8", icon: "box", frontKind: "door", template: "washer", material: "metal" },
  { name: "Stove", category: "appliance", width: 30, depth: 26, height: 36, color: "#a9adb6", icon: "box", frontKind: "door", template: "stove", material: "metal" },

  // Countertops. Straight runs are ~25" deep (24" cabinet + overhang) at 36"
  // — bath vanities a little shallower, bars at 42". L / U sizes are the
  // bounding box; the slab itself stays ~25" (see the counter template).
  { name: "Kitchen counter", category: "countertop", width: 96, depth: 25, height: 36, color: "#f3f1ec", icon: "counter", frontKind: "none", template: "counter", params: { layout: "straight", sink: "single", backsplash: true }, material: "quartz" },
  { name: "Corner counter", category: "countertop", width: 120, depth: 96, height: 36, color: "#c4b5a0", icon: "counter", frontKind: "none", template: "counter", params: { layout: "l-right", sink: "single", backsplash: true }, material: "granite" },
  { name: "U-shaped counter", category: "countertop", width: 144, depth: 108, height: 36, color: "#e6dcc8", icon: "counter", frontKind: "none", template: "counter", params: { layout: "u", sink: "double", backsplash: true }, material: "quartzite" },
  { name: "Bathroom vanity", category: "countertop", width: 48, depth: 22, height: 34, color: "#ececea", icon: "counter", frontKind: "none", template: "counter", params: { layout: "straight", sink: "single", backsplash: true }, material: "marble" },
  { name: "Double vanity", category: "countertop", width: 72, depth: 22, height: 34, color: "#f7f5f2", icon: "counter", frontKind: "none", template: "counter", params: { layout: "straight", sink: "double", backsplash: true }, material: "tile" },
  { name: "Bar counter", category: "countertop", width: 72, depth: 18, height: 42, color: "#e2c49a", icon: "counter", frontKind: "none", template: "counter", params: { layout: "straight", sink: "none", backsplash: false }, material: "butcher-block" },
  { name: "Laundry counter", category: "countertop", width: 60, depth: 25, height: 36, color: "#e4e0d8", icon: "counter", frontKind: "none", template: "counter", params: { layout: "straight", sink: "single", backsplash: true }, material: "laminate" },
  { name: "Outdoor counter", category: "countertop", width: 84, depth: 28, height: 36, color: "#c8c6c1", icon: "counter", frontKind: "none", template: "counter", params: { layout: "straight", sink: "none", backsplash: true }, material: "concrete" },
  { name: "Kitchen island", category: "countertop", width: 72, depth: 40, height: 36, color: "#eceae6", icon: "counter", frontKind: "none", template: "island", params: { seating: false, sink: "single" }, material: "terrazzo" },
  { name: "Island with seating", category: "countertop", width: 84, depth: 48, height: 36, color: "#3e4544", icon: "counter", frontKind: "none", template: "island", params: { seating: true, sink: "single" }, material: "soapstone" },

  // Decor
  { name: "Area rug 5x7", category: "decor", width: 60, depth: 84, height: 1, color: "#c39a72", icon: "rug", frontKind: "none", template: "rug", material: "wool-rug" },
  { name: "Area rug 8x10", category: "decor", width: 96, depth: 120, height: 1, color: "#c39a72", icon: "rug", frontKind: "none", template: "rug", material: "wool-rug" },
  { name: "Floor lamp", category: "decor", width: 18, depth: 18, height: 60, color: "#e0d8c8", icon: "lamp", frontKind: "none", template: "lamp", material: "matte" },
  { name: "Plant (large)", category: "decor", width: 24, depth: 24, height: 60, color: "#5e8a5e", icon: "plant", frontKind: "none", template: "plant", material: "foliage" },
];

/** O(1) lookup from preset name to the full preset (used to upgrade legacy
 *  designs that pre-date the parametric model). */
const NAME_TO_PRESET: ReadonlyMap<string, FurniturePreset> = new Map(
  FURNITURE_PRESETS.map((p) => [p.name, p])
);

export function presetForName(name: string): FurniturePreset | undefined {
  return NAME_TO_PRESET.get(name);
}

/** O(1) lookup from preset name to its `frontKind`. Custom user pieces
 *  (renamed or unknown to the catalog) fall through to `"none"`. */
const NAME_TO_FRONT_KIND: ReadonlyMap<string, FrontKind> = new Map(
  FURNITURE_PRESETS.map((p) => [p.name, p.frontKind])
);

/** Resolve a furniture's `frontKind` by name. Returns `"none"` when the
 *  name doesn't match any catalog preset (custom or renamed pieces) — the
 *  conservative default means the canvas marker stays hidden and the
 *  door-clearance validator stays silent. */
export function frontKindForName(name: string): FrontKind {
  return NAME_TO_FRONT_KIND.get(name) ?? "none";
}

export const MOUNT_PRESETS: MountPreset[] = [
  { name: 'TV 43"', category: "tv", width: 38, height: 22, defaultFromFloor: 48, color: "#1a1a1f" },
  { name: 'TV 55"', category: "tv", width: 48, height: 28, defaultFromFloor: 52, color: "#1a1a1f" },
  { name: 'TV 65"', category: "tv", width: 57, height: 33, defaultFromFloor: 56, color: "#1a1a1f" },
  { name: 'TV 75"', category: "tv", width: 66, height: 38, defaultFromFloor: 60, color: "#1a1a1f" },
  { name: "Art (small)", category: "art", width: 18, height: 24, defaultFromFloor: 60, color: "#a07b54" },
  { name: "Art (medium)", category: "art", width: 30, height: 40, defaultFromFloor: 60, color: "#a07b54" },
  { name: "Art (large)", category: "art", width: 48, height: 36, defaultFromFloor: 60, color: "#8a6a47" },
  { name: "Floating shelf", category: "shelf", width: 36, height: 8, defaultFromFloor: 54, color: "#8a6a47" },
  { name: "Wall mirror", category: "mirror", width: 24, height: 36, defaultFromFloor: 60, color: "#cfd2d8" },
  { name: "Sconce light", category: "lighting", width: 6, height: 12, defaultFromFloor: 66, color: "#e6c97a" },
];

/** Categories shown in the picker. */
export const FURNITURE_CATEGORIES: { id: FurnitureCategory; label: string }[] = [
  { id: "seating", label: "Seating" },
  { id: "tables", label: "Tables" },
  { id: "beds", label: "Beds" },
  { id: "storage", label: "Storage" },
  { id: "appliance", label: "Appliances" },
  { id: "countertop", label: "Countertops" },
  { id: "decor", label: "Decor" },
];

export const MOUNT_CATEGORIES: { id: WallMountCategory; label: string }[] = [
  { id: "tv", label: "TVs" },
  { id: "art", label: "Art" },
  { id: "shelf", label: "Shelves" },
  { id: "mirror", label: "Mirrors" },
  { id: "lighting", label: "Lighting" },
];

/** Real-world average dimensions for openings, in inches. */
export const OPENING_PRESETS: OpeningPreset[] = [
  // Doors sit on the floor, so center = height/2.
  { name: "Door (standard)", kind: "door", width: 36, height: 80, defaultFromFloor: 40 },
  { name: "Door (narrow)", kind: "door", width: 30, height: 80, defaultFromFloor: 40 },
  { name: "Sliding door", kind: "door", width: 72, height: 80, defaultFromFloor: 40 },
  { name: "French doors", kind: "door", width: 60, height: 80, defaultFromFloor: 40 },
  { name: "Wall cutout", kind: "door", style: "cutout", width: 36, height: 80, defaultFromFloor: 40 },
  // Windows hang above the floor.
  { name: "Window (small)", kind: "window", width: 30, height: 36, defaultFromFloor: 48 },
  { name: "Window (medium)", kind: "window", width: 48, height: 48, defaultFromFloor: 48 },
  { name: "Picture window", kind: "window", width: 72, height: 60, defaultFromFloor: 42 },
  { name: "Bay window", kind: "window", width: 96, height: 60, defaultFromFloor: 36 },
];

/** A garden gate: as tall as a privacy fence, wide enough for a mower. */
export const GATE_PRESET: OpeningPreset = { name: "Gate", kind: "door", style: "gate", width: 42, height: 72, defaultFromFloor: 36 };

/** The openings offered for a room. Outdoor rooms get the gate first. */
export function openingPresetsFor(setting: RoomSetting): OpeningPreset[] {
  return setting === "outdoor" ? [GATE_PRESET, ...OPENING_PRESETS] : OPENING_PRESETS;
}

export const OPENING_CATEGORIES: { id: OpeningKind; label: string }[] = [
  { id: "door", label: "Doors" },
  { id: "window", label: "Windows" },
];

const presetPreviewCache = new WeakMap<FurniturePreset, Omit<Furniture, "id">>();

/**
 * The catalog thumbnail for a preset. One object per preset, parked at the
 * origin, so a picker re-render can skip the preview instead of allocating
 * a new piece on every parent render.
 */
export function presetPreviewFurniture(p: FurniturePreset): Omit<Furniture, "id"> {
  let hit = presetPreviewCache.get(p);
  if (!hit) {
    hit = presetToFurniture(p, 0, 0);
    presetPreviewCache.set(p, hit);
  }
  return hit;
}

export function presetToFurniture(p: FurniturePreset, x: number, y: number): Omit<Furniture, "id"> {
  if (p.modular) {
    const centered = centerModules(defaultSectionalModules());
    return {
      name: p.name,
      category: p.category,
      width: centered.width,
      depth: centered.depth,
      height: p.height,
      x,
      y,
      rotation: 0,
      color: p.color,
      icon: p.icon,
      model: { kind: "modular", modules: centered.modules },
      material: { id: p.material, tint: p.color },
      frontKind: p.frontKind,
    };
  }
  return {
    name: p.name,
    category: p.category,
    width: p.width,
    depth: p.depth,
    height: p.height,
    x,
    y,
    rotation: 0,
    color: p.color,
    icon: p.icon,
    model: { kind: "parametric", template: p.template, params: { ...(p.params ?? {}) } },
    material: { id: p.material, tint: p.color },
    frontKind: p.frontKind,
  };
}

/**
 * Build a placed {@link Furniture} from a user-authored
 * {@link CustomFurnitureItem} (the "furniture creator" catalog), copying the
 * vector `shape` outline through so the canvas renders the sketched footprint
 * rather than a plain rectangle.
 *
 * `frontKindForName(item.name)` resolves to `"none"` for arbitrary user names,
 * so the catalog's front-edge marker and the door-clearance validator stay
 * silent for these pieces — the intended behavior for items whose orientation
 * we can't infer.
 */
export function customItemToFurniture(
  item: CustomFurnitureItem,
  x: number,
  y: number
): Omit<Furniture, "id"> {
  return {
    name: item.name,
    category: item.category,
    width: item.width,
    depth: item.depth,
    height: item.height,
    x,
    y,
    rotation: 0,
    color: item.color,
    shape: item.shape.map((p) => ({ x: p.x, y: p.y })),
    ...(item.model ? { model: structuredClone(item.model) } : {}),
    ...(item.material ? { material: { ...item.material } } : {}),
    ...(item.frontKind ? { frontKind: item.frontKind } : {}),
  };
}

export function presetToMount(
  p: MountPreset,
  wallIndex: number,
  alongWall: number
): Omit<WallMount, "id"> {
  return {
    name: p.name,
    category: p.category,
    wallIndex,
    alongWall,
    fromFloor: p.defaultFromFloor,
    width: p.width,
    height: p.height,
    color: p.color,
  };
}

export function presetToOpening(
  p: OpeningPreset,
  wallIndex: number,
  alongWall: number
): Omit<Opening, "id"> {
  // Doors always sit on the floor — center is height/2.
  const fromFloor = p.kind === "door" ? p.height / 2 : p.defaultFromFloor;
  return {
    name: p.name,
    kind: p.kind,
    ...(p.style ? { style: p.style } : {}),
    wallIndex,
    alongWall,
    fromFloor,
    width: p.width,
    height: p.height,
  };
}
